import { triggerErrorMessage } from '@/components/ui/toast';
import { useAppLogger } from '@/observability/useAppLogger';

interface UploadFileResponse {
  file: {
    id: string;
    name: string;
    /** Relative proxy URL, e.g. `/storage/attachments/{id}/{name}` */
    url: string;
  };
}

/**
 * Hook that exposes `uploadFile` — posts a File to the backend attachment
 * endpoint and resolves to the relative proxy URL.
 * Throws on non-2xx responses so callers can show error feedback.
 */
export function useUploadFile() {
  const { logError } = useAppLogger();

  const uploadFile = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);

    let responseStatus: number | undefined;

    try {
      const response = await fetch('/api/app/v1/attachments', {
        method: 'POST',
        credentials: 'include',
        headers: { Accept: 'application/json' },
        body: formData,
      });
      responseStatus = response.status;

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          (errorData as { message?: string }).message ?? `Upload failed (${response.status})`,
        );
      }

      const data = (await response.json()) as UploadFileResponse;
      return data.file.url;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'File upload failed.';

      logError('File upload failed', {
        error: message,
        responseStatus,
        fileSizeBytes: file.size,
        fileType: file.type,
      });
      triggerErrorMessage(message);

      throw error instanceof Error ? error : new Error(message);
    }
  };

  return { uploadFile };
}
